import { v4 as uuidv4 } from 'uuid';
import React, { ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { SendFill } from 'react-bootstrap-icons';
import Markdown from 'react-markdown';
import remarkDirective from 'remark-directive';
import classNames from 'classnames';

import config from '@/lib/config';
import { constructUrl } from '@/lib/utils';
import { getRemarkComponents, remarkCustomTokens } from '@/lib/utils/markdown-utils';
import { messageDataIsAnswer, messageDataIsTypeOfData, MessageEventData } from '@/lib/models/event-stream-models';
import {
	AI_RESPONSE_SOURCE_IDS,
	FollowUpQuestionSuggestion,
	individualResponseSourceOptions,
	isAskHackettSourceSelectionLocked,
} from '@/lib/models';
import { aiConversationService } from '@/lib/services';
import { createUseThemedStyles, useAccount, useEventStreams, useHandleError, useResizeObserver } from '@/hooks';
import { AiSettingsDropdown, LoadingButton, VoiceRecordButton } from '@/components';

const useStyles = createUseThemedStyles((theme) => ({
	chat: {
		width: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: theme.colors.white,
	},
	scrollerOuter: {
		flex: 1,
		overflowY: 'auto',
	},
	scrollerInner: {
		padding: 15,
		width: '100%',
		maxWidth: 768,
		margin: '0 auto',
	},
	chatQuestion: {
		padding: 15,
		borderRadius: 5,
		marginBottom: 25,
		backgroundColor: theme.colors.gray200,
	},
	chatAnswer: {
		'& > h1, & > h2, & > h3, & > h4, & > h5, & > h6, & > p, & > ul, & > ol': {
			marginBottom: 15,
			'& > li': {
				marginBottom: 15,
				'&:last-of-type': {
					marginBottom: 0,
				},
			},
		},
	},
	formOuter: {
		width: '100%',
		maxWidth: 768,
		margin: '0 auto',
		padding: '0 15px 15px 15px',
	},
	textareaOuter: {
		borderRadius: 5,
		marginBottom: 15,
		padding: '7px 15px 15px 15px',
		border: `1px solid ${theme.colors.gray500}`,
		'&:focus-within': {
			border: `1px solid ${theme.colors.primary}`,
		},
		'& textarea.hackett-form__control': {
			flex: 1,
			border: 0,
			height: 39,
			padding: '8px 0',
			boxShadow: 'none',
			'&:disabled': {
				border: 0,
				backgroundColor: 'transparent',
			},
		},
	},
	thinking: {
		color: 'transparent',
		backgroundClip: 'text',
		display: 'inline-block',
		backgroundSize: '48px 100%',
		backgroundColor: theme.colors.black,
		animation: 'think 1s infinite linear',
		background: `-webkit-gradient(linear, left top, right top, from(${theme.colors.black}), to(${theme.colors.black}), color-stop(0.5, ${theme.colors.white})) no-repeat`,
	},
	'@global': {
		'@keyframes think': {
			'0%': {
				backgroundPosition: '-48px 0%',
			},
			'100%': {
				backgroundPosition: 'calc(100% + 48px) 0%',
			},
		},
	},
}));

interface AiChatProps {
	noConversationState: ReactNode;
	onQuerySubmitted?(): void;
	queryContext?: Record<string, string>;
	hideSourcesDropdown?: boolean;
}

export interface AiChatRef {
	setMessage: React.Dispatch<React.SetStateAction<string>>;
	reset(config?: { responses?: Responses; aiResponseSourceIds?: AI_RESPONSE_SOURCE_IDS[] }): void;
}

type Responses = {
	id: string;
	aiConversationId?: string;
	conversationQueryId?: string;
	question: string;
	markdown: string;
	typeOfData: string;
	streamStatus?: string;
}[];

export const AiChat = React.forwardRef(
	(
		{ noConversationState, onQuerySubmitted, queryContext, hideSourcesDropdown }: AiChatProps,
		ref: React.Ref<AiChatRef> | undefined
	) => {
		const [searchParams, setSearchParams] = useSearchParams();
		const classes = useStyles();
		const { account } = useAccount();
		const handleError = useHandleError();
		const eventStreams = useEventStreams();
		const textareaRef = useRef<HTMLTextAreaElement>(null);
		const hasReceivedFirstStreamEventRef = useRef(false);
		const textAreaHeightLimit = 88;
		const [isFetchingTicket, setIsFetchingTicket] = useState(false);
		const [isTranscribing, setIsTranscribing] = useState(false);
		const [isStreaming, setIsStreaming] = useState(false);
		const [message, setMessage] = useState('');
		const isPromptEmpty = !message.trim();
		const [suggestionSubmission, setSuggestionSubmission] = useState<FollowUpQuestionSuggestion>();
		const [responses, setResponses] = useState<Responses>([]);
		const hasRunningResponse = responses.some((response) => response.streamStatus === 'RUNNING');
		const { scrollerInnerRef, scrollerOuterRef } = useResizeObserver(hasRunningResponse);

		/*------------------------------------------------------*/
		/* Response Sources */
		/*------------------------------------------------------*/
		const allAiResponseSourceIds = useMemo(
			() => individualResponseSourceOptions.map((option) => option.aiResponseSourceId),
			[]
		);

		const availableAiResponseSourceIds = useMemo(
			() =>
				account?.availableAiResponseSourceIds && account.availableAiResponseSourceIds.length > 0
					? account.availableAiResponseSourceIds
					: allAiResponseSourceIds,
			[account?.availableAiResponseSourceIds, allAiResponseSourceIds]
		);

		const availableSourceOptions = useMemo(
			() =>
				individualResponseSourceOptions.filter((option) =>
					availableAiResponseSourceIds.includes(option.aiResponseSourceId)
				),
			[availableAiResponseSourceIds]
		);
		const shouldHideSourceSelector =
			hideSourcesDropdown || isAskHackettSourceSelectionLocked(availableAiResponseSourceIds);

		const [aiResponseSourceIds, setAiResponseSourceIds] = useState(
			account?.aiPreferences?.aiResponseSourceIds ?? availableAiResponseSourceIds
		);

		useEffect(() => {
			if (responses.length > 0) {
				return;
			}

			setAiResponseSourceIds(account?.aiPreferences?.aiResponseSourceIds ?? availableAiResponseSourceIds);
		}, [account?.aiPreferences, availableAiResponseSourceIds, responses]);

		/*------------------------------------------------------*/
		/* Thinking/processing message */
		/*------------------------------------------------------*/
		const [thinkingMessage, setThinkingMessage] = useState('Working on it...');
		const thinkingTimerRef = useRef<NodeJS.Timeout>();
		const queryPollingTimerRef = useRef<NodeJS.Timeout>();
		const activePollingKeyRef = useRef<string>();
		const hasInlineRunningMessage = responses.some(
			(response) => response.streamStatus === 'RUNNING' && !eventStreams.getEventStreamById(response.id)
		);

		const stopThinkingTimer = useCallback(() => {
			setThinkingMessage('Working on it...');
			if (thinkingTimerRef.current) {
				clearTimeout(thinkingTimerRef.current);
				thinkingTimerRef.current = undefined;
			}
		}, []);

		const stopQueryPolling = useCallback(() => {
			activePollingKeyRef.current = undefined;
			if (queryPollingTimerRef.current) {
				clearTimeout(queryPollingTimerRef.current);
				queryPollingTimerRef.current = undefined;
			}
		}, []);

		const startThinkingTimer = useCallback(() => {
			stopThinkingTimer();

			thinkingTimerRef.current = setTimeout(() => {
				setThinkingMessage('Still working on it...');
			}, 7000);
		}, [stopThinkingTimer]);

		/*------------------------------------------------------*/
		/* Expose methods that can be called externally */
		/*------------------------------------------------------*/
		const reset = useCallback(
			(config?: { responses?: Responses; aiResponseSourceIds?: AI_RESPONSE_SOURCE_IDS[] }) => {
				eventStreams.closeAllEventStreams();
				stopQueryPolling();
				setIsFetchingTicket(false);
				setIsStreaming(false);
				setMessage('');
				setSuggestionSubmission(undefined);
				setResponses(config?.responses ?? []);
				setAiResponseSourceIds(
					config?.aiResponseSourceIds ??
						account?.aiPreferences?.aiResponseSourceIds ??
						availableAiResponseSourceIds
				);
				stopThinkingTimer();
			},
			[
				account?.aiPreferences?.aiResponseSourceIds,
				availableAiResponseSourceIds,
				eventStreams,
				stopQueryPolling,
				stopThinkingTimer,
			]
		);

		useImperativeHandle(
			ref,
			() => ({
				setMessage,
				reset,
			}),
			[reset]
		);

		/*------------------------------------------------------*/
		/* Stream and post handling */
		/*------------------------------------------------------*/
		const mergeConversationQueriesToResponses = useCallback(
			(
				previousResponses: Responses,
				conversationQueries: {
					aiConversationId: string;
					conversationQueryId: string;
					query: string;
					queryResponse: string;
					streamStatus?: string;
				}[]
			) =>
				conversationQueries.map((conversationQuery) => {
					const existingResponse = previousResponses.find(
						(response) => response.conversationQueryId === conversationQuery.conversationQueryId
					);
					const shouldPreserveLiveMarkdown =
						conversationQuery.streamStatus === 'RUNNING' && !conversationQuery.queryResponse;

					return {
						id:
							shouldPreserveLiveMarkdown && existingResponse
								? existingResponse.id
								: conversationQuery.conversationQueryId,
						aiConversationId: conversationQuery.aiConversationId,
						conversationQueryId: conversationQuery.conversationQueryId,
						question: conversationQuery.query,
						markdown: shouldPreserveLiveMarkdown
							? existingResponse?.markdown ?? ''
							: conversationQuery.queryResponse ?? '',
						typeOfData: shouldPreserveLiveMarkdown ? existingResponse?.typeOfData ?? '' : '',
						streamStatus: conversationQuery.streamStatus,
					};
				}),
			[]
		);

		const handleStreamMessage = useCallback((streamId: string, data?: string) => {
			if (!data) {
				return;
			}

			const parsedData: MessageEventData = JSON.parse(data);

			if (messageDataIsTypeOfData(parsedData)) {
				setResponses((previousValue) =>
					previousValue.map((i) =>
						i.id === streamId
							? {
									...i,
									typeOfData: parsedData.content,
									streamStatus: 'RUNNING',
							  }
							: i
					)
				);
			}

			if (messageDataIsAnswer(parsedData)) {
				setResponses((previousValue) =>
					previousValue.map((i) =>
						i.id === streamId
							? {
									...i,
									markdown: i.markdown.concat(parsedData.content),
									streamStatus: 'RUNNING',
							  }
							: i
					)
				);
			}
		}, []);

		const cleanUpStream = useCallback(() => {
			setIsStreaming(false);
			setIsFetchingTicket(false);
			hasReceivedFirstStreamEventRef.current = false;
			stopThinkingTimer();
			setTimeout(() => {
				textareaRef.current?.focus();
			}, 0);
		}, [stopThinkingTimer]);

		const startPollingForQuery = useCallback(
			(aiConversationId: string, conversationQueryId: string) => {
				const pollingKey = `${aiConversationId}:${conversationQueryId}`;
				if (activePollingKeyRef.current === pollingKey) {
					return;
				}

				stopQueryPolling();
				activePollingKeyRef.current = pollingKey;

				const poll = async () => {
					try {
						const response = await aiConversationService.getConversation(aiConversationId).fetch();
						const currentQuery = response.aiConversation.conversationQueries.find(
							(query) => query.conversationQueryId === conversationQueryId
						);

						setResponses((previousResponses) =>
							mergeConversationQueriesToResponses(
								previousResponses,
								response.aiConversation.conversationQueries
							)
						);

						if (searchParams.get('aiConversationId') !== aiConversationId) {
							const nextSearchParams = new URLSearchParams(searchParams);
							nextSearchParams.set('aiConversationId', aiConversationId);
							setSearchParams(nextSearchParams);
						}

						if (currentQuery?.streamStatus === 'RUNNING') {
							queryPollingTimerRef.current = setTimeout(() => {
								void poll();
							}, 2000);
							return;
						}

						stopQueryPolling();
					} catch (error) {
						stopQueryPolling();
						handleError(error);
					}
				};

				void poll();
			},
			[handleError, mergeConversationQueriesToResponses, searchParams, setSearchParams, stopQueryPolling]
		);

		const handleFormSubmit = useCallback(
			async (event?: React.FormEvent<HTMLFormElement>) => {
				event?.preventDefault();
				if (isPromptEmpty) {
					return;
				}

				setIsStreaming(true);
				setIsFetchingTicket(true);
				startThinkingTimer();

				try {
					const streamId = uuidv4();
					const aiConversationId = searchParams.get('aiConversationId') ?? undefined;
					hasReceivedFirstStreamEventRef.current = false;

					setResponses((previousValue) =>
						previousValue.concat({
							id: streamId,
							question: message,
							markdown: '',
							typeOfData: '',
							streamStatus: 'RUNNING',
						})
					);
					setMessage('');

					const { query } = await aiConversationService
						.getConversationTicket({
							query: message,
							aiResponseSourceIds,
							shouldShowSuggestions:
								typeof account?.aiPreferences?.showFollowUpQuestions === 'boolean'
									? account?.aiPreferences?.showFollowUpQuestions
									: true,
							...(suggestionSubmission && {
								suggestion: { ...suggestionSubmission },
							}),
							...(aiConversationId && {
								aiConversationId,
							}),
							...(queryContext && { ...queryContext }),
						})
						.fetch();

					setResponses((previousValue) =>
						previousValue.map((response) =>
							response.id === streamId
								? {
										...response,
										aiConversationId: query.aiConversationId,
										conversationQueryId: query.conversationQueryId,
										streamStatus: 'RUNNING',
								  }
								: response
						)
					);

					onQuerySubmitted?.();

					const advisoryBaseUrl = config.HACKETT_ADVISORY_BASE_URL.replace(/\/+$/, '');
					eventStreams.openEventStream(
						constructUrl(`${advisoryBaseUrl}/advisory-service/api/v1/public/ai-conversation/stream`, {
							ticket: query.ticket,
						}),
						{
							streamId,
							onOpen: () => {
								setIsStreaming(true);
							},
							onMessage: (incomingStreamId, data) => {
								if (!hasReceivedFirstStreamEventRef.current) {
									hasReceivedFirstStreamEventRef.current = true;
									setIsFetchingTicket(false);
									stopThinkingTimer();
								}

								handleStreamMessage(incomingStreamId, data);
							},
							onComplete: () => {
								cleanUpStream();
							},
							onError: (error) => {
								console.error(error);
								cleanUpStream();
							},
						}
					);
				} catch (error) {
					handleError(error);
					cleanUpStream();
				} finally {
					setSuggestionSubmission(undefined);
				}
			},
			[
				account?.aiPreferences?.showFollowUpQuestions,
				aiResponseSourceIds,
				cleanUpStream,
				eventStreams,
				handleError,
				handleStreamMessage,
				isPromptEmpty,
				message,
				onQuerySubmitted,
				queryContext,
				searchParams,
				startThinkingTimer,
				stopThinkingTimer,
				suggestionSubmission,
			]
		);

		const updateQuestionInputAndFocus = useCallback(
			(inputText: string) => {
				setMessage((prev) => (prev ? `${prev} ${inputText}` : inputText));
				requestAnimationFrame(() => {
					const ta = textareaRef.current;
					if (!ta) return;
					ta.style.height = '';
					ta.style.height = `${Math.min(ta.scrollHeight, textAreaHeightLimit)}px`;
					ta.focus();
					try {
						const len = message.length;
						ta.selectionStart = len;
						ta.selectionEnd = len;
						ta.scrollTop = ta.scrollHeight;
					} catch {}
				});
			},
			[message.length]
		);

		const handleDictation = useCallback(
			async (file: File) => {
				try {
					setIsTranscribing(true);
					const { transcript } = await aiConversationService.voiceToText(file).fetch();
					if (transcript) {
						updateQuestionInputAndFocus(transcript);
					}
				} catch (e) {
					handleError(e);
				} finally {
					setIsTranscribing(false);
				}
			},
			[handleError, updateQuestionInputAndFocus]
		);

		useEffect(() => {
			const runningResponse = [...responses]
				.reverse()
				.find(
					(response) =>
						response.streamStatus === 'RUNNING' && response.aiConversationId && response.conversationQueryId
				);

			if (!runningResponse) {
				stopQueryPolling();
				return;
			}

			if (eventStreams.getEventStreamById(runningResponse.id)) {
				return;
			}

			startPollingForQuery(runningResponse.aiConversationId!, runningResponse.conversationQueryId!);
		}, [eventStreams, responses, startPollingForQuery, stopQueryPolling]);

		/*------------------------------------------------------*/
		/* Remark handlers */
		/*------------------------------------------------------*/
		const remarkComponents = useMemo(
			() =>
				getRemarkComponents({
					onSuggestionClick: (suggestion) => {
						setMessage(suggestion.suggestion);
						setSuggestionSubmission(suggestion);
					},
					getAiConversationId: () => {
						return searchParams.get('aiConversationId') ?? '';
					},
				}),
			[searchParams]
		);

		/*------------------------------------------------------*/
		/* Textarea functionality */
		/*------------------------------------------------------*/
		useEffect(() => {
			textareaRef.current?.focus();
		}, []);

		useEffect(
			() => () => {
				stopQueryPolling();
			},
			[stopQueryPolling]
		);

		useEffect(() => {
			const { current } = textareaRef;
			const handleTextareaKeypress = (event: KeyboardEvent) => {
				if (event.key === 'Enter' && !event.shiftKey) {
					event.preventDefault();

					if (textareaRef.current) {
						textareaRef.current.style.height = '';
					}

					handleFormSubmit();
				}
			};

			current?.addEventListener('keypress', handleTextareaKeypress);

			return () => {
				current?.removeEventListener('keypress', handleTextareaKeypress);
			};
		}, [handleFormSubmit]);

		const handleTextareaInput = useCallback(() => {
			if (!textareaRef.current) {
				return;
			}

			textareaRef.current.style.height = '';
			textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, textAreaHeightLimit)}px`;
		}, []);

		const handleTextareaChange = useCallback(({ currentTarget }: React.ChangeEvent<HTMLTextAreaElement>) => {
			setMessage(currentTarget.value);
			setSuggestionSubmission(undefined);
		}, []);

		return (
			<div className={classes.chat}>
				<div ref={scrollerOuterRef} className={classes.scrollerOuter}>
					<div ref={scrollerInnerRef} className={classes.scrollerInner}>
						{responses.length <= 0
							? noConversationState
							: responses.map((response, responseIndex) => {
									const isLast = responses.length - 1 === responseIndex;

									return (
										<div
											key={response.id}
											className={classNames({
												'mb-3': !isLast,
											})}
										>
											<div className={classes.chatQuestion}>{response.question}</div>
											<div className={classNames(classes.chatAnswer, 'chat-answer-to-copy')}>
												<Markdown
													remarkPlugins={[remarkDirective, remarkCustomTokens]}
													components={remarkComponents}
												>
													{response.markdown}
												</Markdown>
												{response.streamStatus === 'RUNNING' &&
													!eventStreams.getEventStreamById(response.id) && (
														<p className={classes.thinking}>
															Still working on this answer...
														</p>
													)}
											</div>
										</div>
									);
							  })}
						{isFetchingTicket && !hasInlineRunningMessage && (
							<p className={classes.thinking}>{thinkingMessage}</p>
						)}
					</div>
				</div>
				<div className={classes.formOuter}>
					<Form onSubmit={handleFormSubmit}>
						<fieldset className={classes.textareaOuter} disabled={isStreaming}>
							<Form.Control
								ref={textareaRef}
								as="textarea"
								placeholder="Message AskHackett™"
								value={message}
								onInput={handleTextareaInput}
								onChange={handleTextareaChange}
							/>
							<div className="d-flex align-items-center justify-content-between">
								{shouldHideSourceSelector ? (
									<div />
								) : (
									<AiSettingsDropdown
										aiResponseSourceIds={aiResponseSourceIds}
										sourceOptions={availableSourceOptions}
										onChange={setAiResponseSourceIds}
										disabled={responses.length > 0}
									/>
								)}

								<div className="d-flex align-items-center ml-auto">
									<VoiceRecordButton
										className="p-2"
										isDisabled={isStreaming}
										silenceDebounceMs={1500}
										title="Dictate"
										onRecorded={handleDictation}
										isTranscribing={isTranscribing}
									/>
									<LoadingButton
										isLoading={isStreaming}
										type="submit"
										className="p-2"
										disabled={isPromptEmpty}
									>
										<SendFill className="d-flex" width={20} height={20} />
									</LoadingButton>
								</div>
							</div>
						</fieldset>
						<p className="mb-0 text-muted text-center small">
							AI-generated content may be incomplete or inaccurate.
						</p>
					</Form>
				</div>
			</div>
		);
	}
);
